import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useFornecedor } from '@/features/compras/hooks/useFornecedor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PAGE_SIZES = [10, 20, 50] as const;

const Fornecedores = () => {
  const navigate = useNavigate();
  const [busca,          setBusca]          = useState('');
  const [pagina,         setPagina]         = useState(1);
  const [tamanhoPagina,  setTamanhoPagina]  = useState(20);

  const { fornecedores, total, totalPaginas, isLoading } =
    useFornecedor({ pagina, tamanhoPagina });

  const filtrados = busca
    ? fornecedores.filter(f =>
        f.nomeRazao.toLowerCase().includes(busca.toLowerCase()) ||
        f.cpfCnpj?.includes(busca))
    : fornecedores;

  const ativos   = fornecedores.filter(f =>  f.isAtivo).length;
  const inativos = fornecedores.filter(f => !f.isAtivo).length;
  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Fornecedores</h1>
          <p className="text-muted-foreground mt-1">Gerencie os fornecedores cadastrados</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/compras/fornecedores/novo')}>
          <Plus className="h-4 w-4" />Novo Fornecedor
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-500" />Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">Fornecedores cadastrados</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{ativos}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Inativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{inativos}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex gap-3">
        <Input
          placeholder="Buscar por nome ou CNPJ..."
          value={busca}
          onChange={e => { setBusca(e.target.value); setPagina(1); }}
          className="max-w-xs"
        />
      </div>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome / Razão Social</TableHead>
                <TableHead>CPF/CNPJ</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-16">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filtrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nenhum fornecedor encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filtrados.map(f => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">
                      {f.nomeRazao}
                      {f.apelido && <span className="text-muted-foreground text-xs ml-1">({f.apelido})</span>}
                    </TableCell>
                    <TableCell>{f.cpfCnpj ?? '—'}</TableCell>
                    <TableCell>{f.email ?? '—'}</TableCell>
                    <TableCell>{f.fone ?? f.celular ?? '—'}</TableCell>
                    <TableCell>
                      <Badge className={f.isAtivo
                        ? 'bg-green-500/10 text-green-600'
                        : 'bg-red-500/10 text-red-600'}>
                        {f.isAtivo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Visualizar"
                        onClick={() => navigate(`/compras/fornecedores/${f.id}`)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Itens por página:</span>
          <Select value={String(tamanhoPagina)} onValueChange={v => { setTamanhoPagina(Number(v)); setPagina(1); }}>
            <SelectTrigger className="w-16 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {total > 0 && <span>{firstItem}–{lastItem} de {total}</span>}
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex items-center px-3 text-sm">{pagina} / {totalPaginas}</span>
          <Button variant="outline" size="sm" disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Fornecedores;
